import React, { useMemo, useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Box,
  CircularProgress,
  Chip,
  Tooltip,
  Stack,
  Avatar,
  Card,
  CardContent,
  alpha,
} from '@mui/material';
import {
  Edit as EditIcon,
  AdminPanelSettings as AdminIcon,
  Add as AddIcon,
  Lock as LockIcon,
  LockOpen as LockOpenIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { format } from 'date-fns';
import * as api from '../../services/api';
import { useI18n } from '../../i18n/useI18n';

const StaffManagement: React.FC = () => {
  const { admin: a, pages: t } = useI18n();
  const [staff, setStaff] = useState<api.AdminUserResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [selectedStaff, setSelectedStaff] = useState<api.AdminUserResponse | null>(null);
  const [editForm, setEditForm] = useState({
    username: '',
    email: '',
  });
  const [createForm, setCreateForm] = useState({
    username: '',
    email: '',
    password: '',
  });

  const stats = useMemo(() => {
    const total = staff.length;
    const active = staff.filter((s) => s.status === 'ACTIVE').length;
    const blocked = staff.filter((s) => s.status !== 'ACTIVE').length;
    return { total, active, blocked };
  }, [staff]);

  const fetchStaff = async () => {
    try {
      setLoading(true);
      const data = await api.getAdminStaff();
      setStaff(data);
    } catch (error) {
      console.error('Error fetching staff:', error);
      toast.error('Failed to load staff members');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStaff();
  }, []);

  const handleCreateDialogOpen = () => setCreateDialogOpen(true);

  const handleCreateDialogClose = () => {
    setCreateDialogOpen(false);
    setCreateForm({ username: '', email: '', password: '' });
  };

  const handleCreateSubmit = async () => {
    try {
      if (!createForm.username || !createForm.email || !createForm.password) {
        toast.error('Please fill in all fields');
        return;
      }
      await api.createStaff(createForm);
      toast.success('Staff account created successfully');
      handleCreateDialogClose();
      fetchStaff();
    } catch (error: any) {
      console.error('Error creating staff:', error);
      toast.error(error.response?.data?.error || 'Failed to create staff account');
    }
  };

  const handleEditClick = (staffMember: api.AdminUserResponse) => {
    setSelectedStaff(staffMember);
    setEditForm({
      username: staffMember.username,
      email: staffMember.email,
    });
    setEditDialogOpen(true);
  };

  const handleEditClose = () => {
    setEditDialogOpen(false);
    setSelectedStaff(null);
    setEditForm({ username: '', email: '' });
  };

  const handleEditSubmit = async () => {
    if (!selectedStaff) return;
    try {
      await api.updateUser(selectedStaff._id, editForm);
      toast.success('Staff member updated successfully');
      fetchStaff();
      handleEditClose();
    } catch (error) {
      console.error('Error updating staff member:', error);
      toast.error('Failed to update staff member');
    }
  };

  const handlePromoteToAdmin = async (staffId: string) => {
    try {
      await api.promoteStaffToAdmin(staffId);
      toast.success('Staff member promoted to admin successfully');
      fetchStaff();
    } catch (error) {
      console.error('Error promoting staff to admin:', error);
      toast.error('Failed to promote staff member');
    }
  };

  const handleToggleStatus = async (staffId: string, currentStatus: string) => {
    try {
      await api.toggleStaffStatus(staffId);
      toast.success(
        `Staff account ${currentStatus === 'ACTIVE' ? 'blocked' : 'activated'} successfully`,
      );
      fetchStaff();
    } catch (error) {
      console.error('Error toggling staff status:', error);
      toast.error('Failed to update staff status');
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  const fieldSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: 2.5,
      bgcolor: alpha('#f48fb1', 0.04),
    },
  };

  return (
    <Container
      maxWidth="lg"
      sx={{
        py: { xs: 2, md: 4 },
        px: { xs: 1.5, sm: 3 },
        '@keyframes smSoftIn': {
          from: { opacity: 0, transform: 'translateY(10px)' },
          to: { opacity: 1, transform: 'translateY(0)' },
        },
      }}
    >
      <Paper
        elevation={0}
        sx={{
          mb: { xs: 1.5, md: 3 },
          p: { xs: 2, md: 3 },
          borderRadius: 4,
          color: 'common.white',
          background: (theme) =>
            `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 45%, ${theme.palette.secondary.main} 100%)`,
          boxShadow: (theme) => `0 20px 45px ${alpha(theme.palette.primary.main, 0.28)}`,
          animation: 'smSoftIn 0.5s ease both',
        }}
      >
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={{ xs: 1.5, md: 2 }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', md: 'center' }}
        >
          <Box>
            <Typography
              variant="overline"
              sx={{ letterSpacing: 1.2, opacity: 0.85, display: { xs: 'none', sm: 'block' } }}
            >
              Admin Dashboard
            </Typography>
            <Typography
              variant="h4"
              sx={{ fontWeight: 800, mb: { xs: 0, md: 0.5 }, fontSize: { xs: '1.5rem', md: '2.125rem' } }}
            >
              {a.staffManagement.title}
            </Typography>
            <Typography sx={{ maxWidth: 620, opacity: 0.9, display: { xs: 'none', sm: 'block' } }}>
              Quản lý nhân viên — xem, sửa và thăng cấp admin
            </Typography>
          </Box>

          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreateDialogOpen}
            sx={{
              minWidth: { xs: 0, md: 210 },
              alignSelf: { xs: 'stretch', md: 'center' },
              bgcolor: 'common.white',
              color: 'primary.main',
              borderRadius: 999,
              px: 2.5,
              py: { xs: 1, md: 1.25 },
              fontWeight: 700,
              boxShadow: '0 10px 24px rgba(0,0,0,0.14)',
              '&:hover': { bgcolor: 'grey.100' },
            }}
          >
            {a.staffManagement.addStaff}
          </Button>
        </Stack>
      </Paper>

      <Box
        sx={{
          mb: { xs: 1.5, md: 3 },
          display: 'grid',
          gap: { xs: 1, sm: 2 },
          gridTemplateColumns: { xs: 'repeat(3, minmax(0, 1fr))', md: 'repeat(3, 1fr)' },
        }}
      >
        {[
          {
            label: 'Tổng nhân viên',
            shortLabel: 'Tổng',
            value: stats.total,
            tone: 'primary.main',
            bg: (theme: any) => alpha(theme.palette.primary.main, 0.08),
          },
          {
            label: 'Đang hoạt động',
            shortLabel: 'Hoạt động',
            value: stats.active,
            tone: 'success.main',
            bg: (theme: any) => alpha(theme.palette.success.main, 0.1),
          },
          {
            label: 'Đã chặn',
            shortLabel: 'Đã chặn',
            value: stats.blocked,
            tone: 'error.main',
            bg: (theme: any) => alpha(theme.palette.error.main, 0.1),
          },
        ].map((item, index) => (
          <Card
            key={item.label}
            elevation={0}
            sx={{
              borderRadius: { xs: 2, md: 3 },
              border: '1px solid',
              borderColor: 'divider',
              bgcolor: item.bg,
              minWidth: 0,
              animation: 'smSoftIn 0.45s ease both',
              animationDelay: `${0.06 + index * 0.05}s`,
            }}
          >
            <CardContent sx={{ p: { xs: 1, sm: 2.25 }, '&:last-child': { pb: { xs: 1, sm: 2.25 } } }}>
              <Typography
                variant="body2"
                sx={{
                  color: 'text.secondary',
                  mb: { xs: 0.25, sm: 1 },
                  fontSize: { xs: '0.65rem', sm: '0.875rem' },
                  lineHeight: 1.25,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>
                  {item.shortLabel}
                </Box>
                <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                  {item.label}
                </Box>
              </Typography>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 800,
                  color: item.tone,
                  fontSize: { xs: '1.15rem', sm: '2.125rem' },
                  lineHeight: 1.2,
                }}
              >
                {item.value}
              </Typography>
            </CardContent>
          </Card>
        ))}
      </Box>

      <Paper
        elevation={0}
        sx={{
          borderRadius: 4,
          border: '1px solid',
          borderColor: 'divider',
          overflow: 'hidden',
          animation: 'smSoftIn 0.5s ease both',
          animationDelay: '0.22s',
        }}
      >
        <Box
          sx={{
            px: 2.25,
            py: 1.75,
            borderBottom: '1px solid',
            borderColor: 'divider',
            bgcolor: (theme) => alpha(theme.palette.primary.main, 0.03),
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 700, fontSize: { xs: '1rem', md: '1.25rem' } }}>
            Danh sách nhân viên
          </Typography>
        </Box>

        {/* Mobile cards */}
        <Box sx={{ display: { xs: 'block', md: 'none' }, p: 1.5 }}>
          <Stack spacing={1.25}>
            {staff.length === 0 ? (
              <Box sx={{ py: 5, textAlign: 'center' }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                  Chưa có nhân viên
                </Typography>
              </Box>
            ) : (
              staff.map((staffMember, index) => (
                <Box
                  key={staffMember._id}
                  sx={{
                    p: 1.5,
                    borderRadius: 3,
                    border: '1px solid',
                    borderColor: 'divider',
                    bgcolor: 'background.paper',
                    animation: 'smSoftIn 0.4s ease both',
                    animationDelay: `${0.28 + Math.min(index, 12) * 0.04}s`,
                  }}
                >
                  <Stack direction="row" spacing={1.25} alignItems="flex-start">
                    <Avatar
                      sx={{
                        width: 44,
                        height: 44,
                        bgcolor: 'primary.main',
                        fontSize: '1.05rem',
                        flexShrink: 0,
                      }}
                    >
                      {staffMember.username?.charAt(0)?.toUpperCase()}
                    </Avatar>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                        <Typography
                          variant="subtitle2"
                          sx={{
                            fontWeight: 700,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {staffMember.username}
                        </Typography>
                        <Chip
                          label={staffMember.role}
                          size="small"
                          sx={{
                            height: 22,
                            fontWeight: 700,
                            fontSize: '0.68rem',
                            borderRadius: 1.5,
                            bgcolor: (theme) => alpha(theme.palette.info.main, 0.12),
                            color: 'info.dark',
                            flexShrink: 0,
                          }}
                        />
                      </Stack>

                      <Typography
                        variant="body2"
                        sx={{
                          color: 'text.secondary',
                          mt: 0.25,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          fontSize: '0.8rem',
                        }}
                      >
                        {staffMember.email}
                      </Typography>

                      <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        justifyContent="space-between"
                        sx={{ mt: 1.25 }}
                      >
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                          {format(new Date(staffMember.createdAt), 'dd/MM/yyyy HH:mm')}
                        </Typography>
                        <Chip
                          label={staffMember.status}
                          size="small"
                          color={staffMember.status === 'ACTIVE' ? 'success' : 'error'}
                          sx={{ height: 22, fontWeight: 700, fontSize: '0.65rem' }}
                        />
                      </Stack>

                      <Stack direction="row" spacing={0.5} justifyContent="flex-end" sx={{ mt: 1 }}>
                        <Tooltip title={a.staffManagement.edit}>
                          <IconButton
                            size="small"
                            onClick={() => handleEditClick(staffMember)}
                            sx={{ bgcolor: alpha('#f48fb1', 0.1) }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={a.staffManagement.promote}>
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handlePromoteToAdmin(staffMember._id)}
                            sx={{ bgcolor: alpha('#1976d2', 0.08) }}
                          >
                            <AdminIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip
                          title={
                            staffMember.status === 'ACTIVE'
                              ? a.staffManagement.block
                              : a.staffManagement.activate
                          }
                        >
                          <IconButton
                            size="small"
                            color={staffMember.status === 'ACTIVE' ? 'error' : 'success'}
                            onClick={() => handleToggleStatus(staffMember._id, staffMember.status)}
                            sx={{
                              bgcolor:
                                staffMember.status === 'ACTIVE'
                                  ? alpha('#d32f2f', 0.08)
                                  : alpha('#2e7d32', 0.08),
                            }}
                          >
                            {staffMember.status === 'ACTIVE' ? (
                              <LockIcon fontSize="small" />
                            ) : (
                              <LockOpenIcon fontSize="small" />
                            )}
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </Box>
                  </Stack>
                </Box>
              ))
            )}
          </Stack>
        </Box>

        {/* Desktop table */}
        <TableContainer sx={{ display: { xs: 'none', md: 'block' } }}>
          <Table>
            <TableHead>
              <TableRow
                sx={{
                  backgroundColor: 'grey.50',
                  '& .MuiTableCell-head': {
                    fontWeight: 700,
                    color: 'text.primary',
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                  },
                }}
              >
                <TableCell sx={{ py: 1.75 }}>{a.staffManagement.colUsername}</TableCell>
                <TableCell sx={{ py: 1.75 }}>{a.staffManagement.colEmail}</TableCell>
                <TableCell sx={{ py: 1.75 }}>{a.staffManagement.colRole}</TableCell>
                <TableCell sx={{ py: 1.75 }}>{a.staffManagement.colStatus}</TableCell>
                <TableCell sx={{ py: 1.75 }}>{a.staffManagement.colCreatedAt}</TableCell>
                <TableCell sx={{ py: 1.75 }} align="right">
                  {a.staffManagement.colActions}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {staff.map((staffMember, index) => (
                <TableRow
                  key={staffMember._id}
                  sx={{
                    animation: 'smSoftIn 0.4s ease both',
                    animationDelay: `${0.28 + Math.min(index, 12) * 0.04}s`,
                    '& .MuiTableCell-body': {
                      py: 2,
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                    },
                    '&:hover': {
                      backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.035),
                    },
                  }}
                >
                  <TableCell>
                    <Stack direction="row" spacing={1.25} alignItems="center">
                      <Avatar sx={{ width: 36, height: 36, bgcolor: 'primary.main', fontSize: '0.95rem' }}>
                        {staffMember.username?.charAt(0)?.toUpperCase()}
                      </Avatar>
                      <Typography variant="body2" fontWeight={600}>
                        {staffMember.username}
                      </Typography>
                    </Stack>
                  </TableCell>
                  <TableCell sx={{ color: 'text.secondary' }}>{staffMember.email}</TableCell>
                  <TableCell>
                    <Chip
                      label={staffMember.role}
                      size="small"
                      sx={{
                        fontWeight: 700,
                        borderRadius: 2,
                        bgcolor: (theme) => alpha(theme.palette.info.main, 0.12),
                        color: 'info.dark',
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={staffMember.status}
                      size="small"
                      color={staffMember.status === 'ACTIVE' ? 'success' : 'error'}
                      sx={{ fontWeight: 700 }}
                    />
                  </TableCell>
                  <TableCell sx={{ color: 'text.secondary' }}>
                    {format(new Date(staffMember.createdAt), 'dd/MM/yyyy HH:mm')}
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title={a.staffManagement.edit}>
                      <IconButton onClick={() => handleEditClick(staffMember)} size="small">
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={a.staffManagement.promote}>
                      <IconButton
                        onClick={() => handlePromoteToAdmin(staffMember._id)}
                        size="small"
                        color="primary"
                      >
                        <AdminIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip
                      title={
                        staffMember.status === 'ACTIVE'
                          ? a.staffManagement.block
                          : a.staffManagement.activate
                      }
                    >
                      <IconButton
                        onClick={() => handleToggleStatus(staffMember._id, staffMember.status)}
                        size="small"
                        color={staffMember.status === 'ACTIVE' ? 'error' : 'success'}
                      >
                        {staffMember.status === 'ACTIVE' ? (
                          <LockIcon fontSize="small" />
                        ) : (
                          <LockOpenIcon fontSize="small" />
                        )}
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Dialog open={editDialogOpen} onClose={handleEditClose} fullWidth maxWidth="xs">
        <DialogTitle sx={{ fontWeight: 800 }}>{a.staffManagement.editDialogTitle}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField
              label={a.staffManagement.username}
              fullWidth
              value={editForm.username}
              onChange={(e) => setEditForm({ ...editForm, username: e.target.value })}
              sx={fieldSx}
            />
            <TextField
              label={a.staffManagement.email}
              type="email"
              fullWidth
              value={editForm.email}
              onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
              sx={fieldSx}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleEditClose} sx={{ textTransform: 'none', fontWeight: 700 }}>
            {t.common.cancel}
          </Button>
          <Button
            onClick={handleEditSubmit}
            variant="contained"
            sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 2 }}
          >
            {a.staffManagement.saveChanges}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={createDialogOpen} onClose={handleCreateDialogClose} fullWidth maxWidth="xs">
        <DialogTitle sx={{ fontWeight: 800 }}>{a.staffManagement.createDialogTitle}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField
              fullWidth
              label={a.staffManagement.username}
              value={createForm.username}
              onChange={(e) => setCreateForm({ ...createForm, username: e.target.value })}
              required
              sx={fieldSx}
            />
            <TextField
              fullWidth
              label={a.staffManagement.email}
              type="email"
              value={createForm.email}
              onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })}
              required
              sx={fieldSx}
            />
            <TextField
              fullWidth
              label={a.staffManagement.password}
              type="password"
              value={createForm.password}
              onChange={(e) => setCreateForm({ ...createForm, password: e.target.value })}
              required
              sx={fieldSx}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCreateDialogClose} sx={{ textTransform: 'none', fontWeight: 700 }}>
            {t.common.cancel}
          </Button>
          <Button
            onClick={handleCreateSubmit}
            variant="contained"
            sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 2 }}
          >
            {a.staffManagement.create}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default StaffManagement;
